
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  Settings,
  Dumbbell,
  ClipboardList,
  Apple,
  HeartPulse,
  TrendingUp,
  MessageSquare
} from "lucide-react";
import { useMobileMenu } from "@/contexts/MobileMenuContext";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Separator } from "@/components/ui/separator";
import { useTranslations } from "@/hooks/useTranslations";
import { ScrollArea } from "@/components/ui/scroll-area";

type StudentProfile = {
  id: string;
  has_schedule_access?: boolean;
  [key: string]: any;
}

export const StudentDashboardSidebar = () => {
  const location = useLocation();
  const { isMenuOpen, closeMenu } = useMobileMenu();
  const { t } = useTranslations('student');
  const { getPath } = useLanguagePath();
  const { user } = useAuth();

  const { data: studentProfile } = useQuery({
    queryKey: ["student-profile", user?.id],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data, error } = await supabase
        .from("students")
        .select("*")
        .eq("email", user.email)
        .eq("status", "active")
        .single();
        
      if (error) {
        console.error("Error fetching student profile:", error);
        return null;
      }
      return data as StudentProfile;
    },
    enabled: !!user?.email
  });

  const hasScheduleAccess = studentProfile?.has_schedule_access !== false;

  const menuItems = [
    {
      title: t('dashboard.overview'),
      icon: LayoutDashboard,
      href: getPath("/student-dashboard"),
    },
    ...(hasScheduleAccess ? [{
      title: t('dashboard.schedule'),
      icon: Calendar,
      href: getPath("/student-dashboard/schedule"),
    }] : []),
    {
      title: t('dashboard.workouts'),
      icon: Dumbbell,
      href: getPath("/student-dashboard/workouts"),
    },
    {
      title: t('dashboard.sessions'),
      icon: ClipboardList,
      href: getPath("/student-dashboard/training-sessions"),
    },
    {
      title: t('dashboard.nutrition'),
      icon: Apple,
      href: getPath("/student-dashboard/nutrition"),
    },
    {
      title: t('dashboard.assessments'),
      icon: HeartPulse,
      href: getPath("/student-dashboard/assessments"),
    },
    {
      title: t('dashboard.progress'),
      icon: TrendingUp,
      href: getPath("/student-dashboard/progress"),
    },
    {
      title: t('dashboard.messages'),
      icon: MessageSquare,
      href: getPath("/student-dashboard/messages"),
    },
  ];

  const settingsItem = {
    title: t('dashboard.settings'),
    icon: Settings,
    href: getPath("/student-dashboard/settings"),
  };

  const handleLinkClick = () => {
    closeMenu();
  };

  const isPathActive = (href: string) => {
    const currentPath = location.pathname.replace(/^\/pt/, '');
    const pathWithoutPrefix = href.replace(/^\/pt/, '');
    return currentPath === pathWithoutPrefix;
  };

  return (
    <>
      <div 
        className={cn(
          "fixed inset-0 bg-black/30 backdrop-blur-sm z-50 transition-opacity duration-300 md:hidden dark:bg-black/50",
          isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      />
      
      <nav 
        className={cn(
          "fixed top-20 left-0 bottom-20 w-64 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl z-60 transform transition-transform duration-300 ease-out md:translate-x-0 md:hidden border border-slate-100/50 dark:border-gray-700/50 rounded-xl shadow-xl font-mono",
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col justify-between h-full max-h-full p-4">
          <ScrollArea className="flex-1 pr-4">
            <div className="flex flex-col gap-1">
              {menuItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={handleLinkClick}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200",
                    isPathActive(item.href) 
                      ? "bg-gradient-to-r from-flame/10 to-flame/20 text-flame shadow-flame/20" 
                      : "text-slate-600 dark:text-gray-300 hover:bg-flame/5 hover:text-flame dark:hover:bg-gray-700/50"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5",
                    isPathActive(item.href)
                      ? "text-flame"
                      : "text-slate-400 dark:text-gray-400 group-hover:text-flame"
                  )} />
                  {item.title}
                </Link>
              ))}
            </div>
          </ScrollArea>
          <div>
            <Separator className="my-2" />
            <Link
              to={settingsItem.href}
              onClick={handleLinkClick}
              className={cn(
                "flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200",
                isPathActive(settingsItem.href) 
                  ? "bg-gradient-to-r from-flame/10 to-flame/20 text-flame shadow-flame/20" 
                  : "text-slate-600 dark:text-gray-300 hover:bg-flame/5 hover:text-flame dark:hover:bg-gray-700/50"
              )}
            >
              <settingsItem.icon className={cn(
                "h-5 w-5",
                isPathActive(settingsItem.href)
                  ? "text-flame"
                  : "text-slate-400 dark:text-gray-400 group-hover:text-flame"
              )} />
              {settingsItem.title}
            </Link>
          </div>
        </div>
      </nav>
      
      <aside className="fixed top-20 left-4 bottom-2 w-64 hidden md:flex flex-col justify-between border border-slate-100/50 dark:border-gray-700/50 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-mono">
        <div className="flex flex-col justify-between h-full">
          <ScrollArea className="flex-1 px-4 pt-4">
            <div className="flex flex-col gap-1">
              {menuItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                    isPathActive(item.href) 
                      ? "bg-gradient-to-r from-flame/10 to-flame/20 text-flame shadow-flame/20" 
                      : "text-slate-600 dark:text-gray-300 hover:bg-flame/5 hover:text-flame dark:hover:bg-gray-700/50"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5 transition-colors",
                    isPathActive(item.href)
                      ? "text-flame"
                      : "text-slate-400 dark:text-gray-400 group-hover:text-flame"
                  )} />
                  {item.title}
                </Link>
              ))}
            </div>
          </ScrollArea>
          <div className="mb-2 px-4">
            <Separator className="mb-2" />
            <div className="flex flex-col gap-1">
              <Link
                to={settingsItem.href}
                className={cn(
                  "group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                  isPathActive(settingsItem.href) 
                    ? "bg-gradient-to-r from-flame/10 to-flame/20 text-flame shadow-flame/20" 
                    : "text-slate-600 dark:text-gray-300 hover:bg-flame/5 hover:text-flame dark:hover:bg-gray-700/50"
                )}
              >
                <settingsItem.icon className={cn(
                  "h-5 w-5 transition-colors",
                  isPathActive(settingsItem.href)
                    ? "text-flame"
                    : "text-slate-400 dark:text-gray-400 group-hover:text-flame"
                )} />
                {settingsItem.title}
              </Link>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
